// Supplement suggestions on the bi-weekly review. Pure: the API passes in her
// latest scan, her recent log, her goals and the practice's product map
// (src/content/supplement-map.json, written by scripts/import-supplement-map.mjs).
// A suggestion is tied to HER GOALS, never to a scan reading: the scan is only
// context on the review page, and a focus area may only ORDER the suggestions.
// Nothing here invents a product, a match or a claim: every suggestion is a row
// the practice wrote. Suggestions never go to Marie, a model or the OS.

import type { LedgerEntry, Scan, ScanFocus } from './types';
import { GOAL_AREA_ALIASES, MEMBER_GOALS } from './member-goals.mjs';

export { MEMBER_GOALS, GOAL_AREA_ALIASES };
export type ScanLevel = ScanFocus['level'];

export interface SupplementRow {
  /** One of MEMBER_GOALS: the row shows when her goals include it. */
  goal: string;
  productName: string;
  productUrl: string;
  why: string;
  /** Never shown. A log that mentions one of these adds the strong caution. */
  doNotSuggestIf: string[];
}
export interface SupplementMap { rows: SupplementRow[] }

export type SafetyAnswer = 'yes' | 'no' | 'prefer_not';
export const SAFETY_ANSWERS: readonly SafetyAnswer[] = ['yes', 'no', 'prefer_not'];
export const SAFETY_QUESTION = 'Are you pregnant or breastfeeding, do you take any prescription medicines, or are you managing a health condition?';
export const SAFETY_LABELS: Record<SafetyAnswer, string> = { yes: 'Yes', no: 'No', prefer_not: 'Prefer not to say' };

export const SUGGESTION_DISCLOSURE =
  'Wellness ideas to explore, not medical advice. Supplements are not drugs, and the FDA does not review them before they are sold. Consider checking with your provider before you start one. Our practice may earn from purchases.';
export const CAUTION_YES = 'Please check with your provider before you try this, because of your health, pregnancy or medicines.';
export const CAUTION_PREFER_NOT = 'Please check with your provider before you try this, especially if you are pregnant, take medicines or manage a health condition.';
/** Her answer was No, but something in her recent log matches the product's caution list. */
export const CAUTION_LOG = 'Please check with your provider before you try this, because of something in your recent log.';

export interface Suggestion {
  productName: string;
  productUrl: string;
  why: string;
  goal: string;
  because: string;
  caution: string | null;
}

export const MAX_SUGGESTIONS = 3;
/** How far back her log counts for do_not_suggest_if. */
export const LOG_DAYS = 14;

/** Case, spacing and punctuation insensitive. */
export const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();

/** A map is usable only if its URLs are Designs for Health pages. The importer checks more; this is a last guard. */
export function isProductUrl(url: string) {
  try {
    const u = new URL(url);
    const h = u.hostname.toLowerCase();
    return u.protocol === 'https:' && (h === 'designsforhealth.com' || h.endsWith('.designsforhealth.com'));
  } catch { return false; }
}

export const usableRow = (r: SupplementRow) =>
  !!r && MEMBER_GOALS.some((g) => norm(g) === norm(r.goal ?? '')) && !!r.productName?.trim() && !!r.why?.trim() && isProductUrl(r.productUrl ?? '');

/** True when the map has at least one row the app can show. */
export const mapHasRows = (map: SupplementMap) => (map.rows ?? []).some(usableRow);

const recent = (log: LedgerEntry[], today: string) => log.filter((e) => {
  const d = (Date.parse(`${today}T12:00:00`) - Date.parse(`${e.date}T12:00:00`)) / 86400e3;
  return d >= 0 && d < LOG_DAYS;
});

/** Everything free text in her recent log, for the do_not_suggest_if caution trigger. */
function logText(log: LedgerEntry[], today: string): string {
  return recent(log, today).map((e) => [e.meals ?? '', e.digestion ?? '', ...(e.reactions ?? []).flatMap((r) => [r.type, r.note ?? ''])].join(' ')).join(' ');
}

export function cautionFor(answer: SafetyAnswer | undefined, logHit: boolean): string | null {
  if (answer === 'prefer_not') return CAUTION_PREFER_NOT;
  if (answer === 'yes') return CAUTION_YES;
  if (logHit) return CAUTION_LOG;
  return null;
}

/** "More energy" becomes "more energy is one of your goals". */
export const becauseLine = (goal: string) => `You might explore this because ${goal.charAt(0).toLowerCase()}${goal.slice(1)} is one of your goals.`;

const RANK: Record<ScanLevel, number> = { 'needs care': 0, elevated: 1, steady: 2 };

/** Whole-word containment of an alias in a scan area name. */
const hasWord = (area: string, alias: string) => ` ${norm(area)} `.includes(` ${norm(alias)} `);

/**
 * Where a goal sorts: 0 or 1 when a needs care or elevated focus area names it loosely
 * (through GOAL_AREA_ALIASES), 2 otherwise. Ordering only; never a reason to show a product.
 */
export function goalOrder(goal: string, scan: Scan | null): number {
  const key = MEMBER_GOALS.find((g) => norm(g) === norm(goal));
  if (!scan || !key) return RANK.steady;
  const aliases = [key, ...(GOAL_AREA_ALIASES[key] ?? [])];
  let best = RANK.steady;
  for (const f of scan.focusAreas) {
    if (f.level === 'steady') continue;
    if (aliases.some((a) => hasWord(f.area, a))) best = Math.min(best, RANK[f.level]);
  }
  return best;
}

export interface MatchInput {
  scan: Scan | null;
  map: SupplementMap;
  log: LedgerEntry[];
  goals: string[];
  /** YYYY-MM-DD */
  today: string;
  safety?: SafetyAnswer;
}

/**
 * Up to 3 suggestions for one review: rows whose goal is one of hers, one per product.
 * Rows whose goal a needs care or elevated area names come first. No scan, no suggestions.
 */
export function matchSuggestions(i: MatchInput): Suggestion[] {
  if (!i.scan) return [];
  const mine = new Set(i.goals.map(norm));
  const rows = (i.map.rows ?? []).map((r, k) => ({ r, k })).filter(({ r }) => usableRow(r) && mine.has(norm(r.goal)));
  if (!rows.length) return [];
  const text = logText(i.log, i.today);
  rows.sort((a, b) => goalOrder(a.r.goal, i.scan) - goalOrder(b.r.goal, i.scan) || a.k - b.k);
  const out: Suggestion[] = [];
  const seen = new Set<string>();
  for (const { r } of rows) {
    // One card per product: the product name is its identity (several products may share a catalog link).
    const key = norm(r.productName);
    if (seen.has(key)) continue;
    seen.add(key);
    const logHit = r.doNotSuggestIf.some((x) => norm(x) && ` ${norm(text)} `.includes(` ${norm(x)} `));
    const goal = MEMBER_GOALS.find((g) => norm(g) === norm(r.goal))!;
    out.push({ productName: r.productName.trim(), productUrl: r.productUrl, why: r.why.trim(), goal, because: becauseLine(goal), caution: cautionFor(i.safety, logHit) });
    if (out.length >= MAX_SUGGESTIONS) break;
  }
  return out;
}
